import React from 'react';
import { Dropdown, Button, colors } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  ChevronDown,
  DriveFile,
  ChevronUp,
  Clipboard,
  IconCheckmark,
  Delete,
  NoteAdd,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import { useRootStore } from 'store/rootStore';
import { EnrollmentStage } from 'types/enrollmentService';
import { useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { employerRights, celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { toJS } from 'mobx';
import { createStyles } from './styles';

interface Props {
  periodId: string;
  stage: EnrollmentStage;
}

const MoreOptionsDropdown = observer(({ periodId, stage }: Props): JSX.Element => {
  const classes = createStyles();
  const history = useHistory();
  const {
    enrollmentCardStore: { sellEnrollment, deleteEnrollment, collectEnrollmentPeriods },
    userStore: { userRoles },
    domainStoreV2: { domainHeader },
  } = useRootStore();

  const convertedDomain = toJS(domainHeader);

  const redirect = (url: string): void => {
    history.push(url);
  };

  const { groupId } = useParams<{ groupId: string }>();

  const closeDeal = async (): Promise<void> => {
    await sellEnrollment(periodId);
    await collectEnrollmentPeriods(groupId);
  };

  const deletePeriod = async (): Promise<void> => {
    await deleteEnrollment(periodId);
    await collectEnrollmentPeriods(groupId);
  };

  const dropdownSubmittedOptions: DropdownOptionType[] = [
    {
      value: 'closeDeal',
      onOptionClick: (): void => {
        void closeDeal();
      },
      label: (
        <Button type="text" leftIconProp={<IconCheckmark />}>
          Close Deal
        </Button>
      ),
      className: clsx(classes.dropdownOption, classes.closeDealOption),
    },
  ];

  const commonOptions: DropdownOptionType[] = [
    {
      value: 'enrollmentPacket',
      onOptionClick: (): void => {
        redirect(`/enrollment-packet/${periodId}`);
      },
      label: (
        <Button type="text" leftIconProp={<DriveFile />}>
          Enrollment Packet
        </Button>
      ),
      className: classes.dropdownOption,
    },
  ];

  const additionalOutputsOption = {
    value: 'additionalOutputs',
    onOptionClick: (): void => {
      redirect(`/additional-outputs/${periodId}`);
    },
    label: (
      <Button type="text" leftIconProp={<NoteAdd />}>
        Additional Outputs
      </Button>
    ),
    className: classes.dropdownOption,
  };

  const summaryPageOption = {
    value: 'summaryPage',
    onOptionClick: (): void => {
      redirect(`/summary/${periodId}`);
    },
    label: (
      <Button type="text" leftIconProp={<Clipboard />}>
        Summary Page
      </Button>
    ),
    className: classes.dropdownOption,
  };

  const deletePeriodOption: DropdownOptionType = {
    value: 'deletePeriod',
    onOptionClick: (): void => {
      void deletePeriod();
    },
    label: (
      <Button type="text" leftIconProp={<Delete />} color="custom" customColor={colors.red[600]}>
        Delete Period
      </Button>
    ),
    className: classes.dropdownOption,
  };

  if (convertedDomain.properties?.existAdditionalOutputs) {
    commonOptions.push(additionalOutputsOption);
  }

  const dropdownOptions = (enrollmentStage: EnrollmentStage): DropdownOptionType[] => {
    switch (enrollmentStage) {
      case EnrollmentStage.SOLD:
        return employerRights(toJS(userRoles)) ? [...commonOptions] : [...commonOptions, summaryPageOption];
      case EnrollmentStage.SUBMITTED:
        return employerRights(toJS(userRoles))
          ? [...commonOptions]
          : [...commonOptions, summaryPageOption, ...dropdownSubmittedOptions];
      case EnrollmentStage.INSTALLED:
        return celticsRights(toJS(userRoles)) ? [deletePeriodOption] : [];
      default:
        return [];
    }
  };

  return (
    <>
      {dropdownOptions(stage).length ? (
        <Dropdown
          name="moreOptions"
          options={dropdownOptions(stage)}
          optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
          optionsWidth="18.4rem"
          closedElement={
            <Button color="primary" type="opaque" rightIconProp={<ChevronDown />} size="small" width="18.4rem">
              More Options
            </Button>
          }
          openedElement={
            <Button color="primary" rightIconProp={<ChevronUp />} size="small" width="18.4rem">
              More Options
            </Button>
          }
        />
      ) : (
        <></>
      )}
    </>
  );
});

export default MoreOptionsDropdown;
